import React from 'react';
import { Plus, X, Users } from 'lucide-react';

/**
 * SAM-195 follow-up — tier groups editor. New Tailwind component (SAM-175), mounted from
 * ServiceDetailDrawer.tsx.
 *
 * Maps to `service.pricing.tier_groups`, the shape `functions/booking/pricing.py` reads:
 *
 *     {"tier_groups": [
 *       {"id": "guests", "label": {"es": "Huespedes", "en": "Guests"},
 *        "tiers": ["adult", "child"], "max_total": 8, "included": 5}
 *     ]}
 *
 * What it expresses that per-option min/max cannot: a cap on the SUMMED quantity across several
 * booking options. Nothing stops 5 adults + 5 children each passing their own per-option maximum
 * while blowing past the venue's real capacity — a cabana for eight, say.
 *
 * `included` is the cabana case: the first N guests are covered by the base price. Free units are
 * handed out following the order of `tiers` in the group, NOT the order the app happens to send
 * them in, so that order is meaningful here and the editor shows it.
 *
 * Everything is optional and additive: a service with no groups prices exactly as it did before.
 */

export interface TierGroupForm {
  id: string;
  label_es: string;
  label_en: string;
  /** booking_options ids belonging to this group, in priority order for `included`. */
  tiers: string[];
  max_total: string;
  included: string;
}

export interface TierOption {
  id: string;
  label: string;
}

interface ServiceTierGroupsEditorProps {
  groups: TierGroupForm[];
  /** The service's booking options — the only ids a group may reference. */
  options: TierOption[];
  onAdd: () => void;
  onRemove: (index: number) => void;
  onChange: (index: number, field: keyof Omit<TierGroupForm, 'tiers'>, value: string) => void;
  onToggleTier: (index: number, optionId: string) => void;
}

/** Empty, or a whole number >= min. Blank means "not set", which the payload omits. */
const isBlankOrInt = (value: string, min: number) => {
  if (value.trim() === '') return true;
  return /^\d+$/.test(value.trim()) && Number(value) >= min;
};

export const ServiceTierGroupsEditor: React.FC<ServiceTierGroupsEditorProps> = ({
  groups,
  options,
  onAdd,
  onRemove,
  onChange,
  onToggleTier,
}) => {
  const labelFor = (optionId: string) =>
    options.find((o) => o.id === optionId)?.label || optionId;

  const problemsFor = (group: TierGroupForm, index: number): string[] => {
    const problems: string[] = [];
    if (!group.id.trim()) {
      problems.push('An id is required — pricing errors name the group by it.');
    } else if (groups.some((g, i) => i !== index && g.id.trim() === group.id.trim())) {
      problems.push('Another group already uses this id.');
    }
    if (group.tiers.length === 0) problems.push('Pick at least one option for the group.');
    if (!isBlankOrInt(group.max_total, 1)) problems.push('Max total must be a whole number of 1 or more.');
    if (!isBlankOrInt(group.included, 0)) problems.push('Included must be a whole number of 0 or more.');
    // Not fatal for the backend, but it means every unit in the group is free, which is almost
    // never what someone meant to configure.
    if (
      group.max_total.trim() !== '' && group.included.trim() !== '' &&
      isBlankOrInt(group.max_total, 1) && isBlankOrInt(group.included, 0) &&
      Number(group.included) >= Number(group.max_total)
    ) {
      problems.push('Included covers the whole cap, so nothing in this group is ever charged.');
    }
    return problems;
  };

  return (
    <div className="text-sm">
      <p className="text-xs mb-3" style={{ color: 'var(--ink-500)' }}>
        A cap on the <strong>combined</strong> number of guests across several booking options —
        the thing a per-option maximum can't express, since 5 adults and 5 children can each stay
        under their own limit and still exceed what the place holds.
      </p>

      {options.length === 0 ? (
        <p className="text-xs italic mb-3" style={{ color: 'var(--ink-400)' }}>
          Add booking options first — a group is built out of them.
        </p>
      ) : groups.length === 0 ? (
        <p className="text-xs italic mb-3" style={{ color: 'var(--ink-400)' }}>
          No groups — each booking option is capped only by its own minimum and maximum.
        </p>
      ) : (
        <div className="space-y-2 mb-3">
          {groups.map((group, index) => {
            const problems = problemsFor(group, index);
            const invalid = problems.length > 0;
            return (
              <div
                key={index}
                className="rounded-lg p-3"
                style={{
                  border: invalid ? '1px solid #DC2626' : '1px solid var(--line)',
                  background: 'var(--surface-2)',
                }}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-1.5">
                    <Users size={12} style={{ color: 'var(--ink-400)' }} />
                    <span className="text-[11px]" style={{ color: 'var(--ink-500)' }}>
                      Group {index + 1}
                    </span>
                  </div>
                  <button
                    type="button"
                    aria-label={`Remove group ${index + 1}`}
                    onClick={() => onRemove(index)}
                    className="rounded hover:opacity-70"
                    style={{ color: 'var(--ink-400)' }}
                  >
                    <X size={13} />
                  </button>
                </div>

                <div className="grid grid-cols-3 gap-2 mb-2">
                  <label className="flex flex-col gap-1">
                    <span className="text-[11px]" style={{ color: 'var(--ink-500)' }}>Id</span>
                    <input
                      type="text"
                      value={group.id}
                      onChange={(e) => onChange(index, 'id', e.target.value)}
                      placeholder="guests"
                      className="rounded px-2 py-1 text-xs"
                      style={{ border: '1px solid var(--line)' }}
                    />
                  </label>
                  <label className="flex flex-col gap-1">
                    <span className="text-[11px]" style={{ color: 'var(--ink-500)' }}>Label (ES)</span>
                    <input
                      type="text"
                      value={group.label_es}
                      onChange={(e) => onChange(index, 'label_es', e.target.value)}
                      placeholder="Huéspedes"
                      className="rounded px-2 py-1 text-xs"
                      style={{ border: '1px solid var(--line)' }}
                    />
                  </label>
                  <label className="flex flex-col gap-1">
                    <span className="text-[11px]" style={{ color: 'var(--ink-500)' }}>Label (EN)</span>
                    <input
                      type="text"
                      value={group.label_en}
                      onChange={(e) => onChange(index, 'label_en', e.target.value)}
                      placeholder="Guests"
                      className="rounded px-2 py-1 text-xs"
                      style={{ border: '1px solid var(--line)' }}
                    />
                  </label>
                </div>

                <div className="mb-2">
                  <span className="text-[11px] block mb-1" style={{ color: 'var(--ink-500)' }}>
                    Options in this group
                  </span>
                  <div className="flex flex-wrap gap-1.5">
                    {options.map((option) => {
                      const position = group.tiers.indexOf(option.id);
                      const selected = position >= 0;
                      return (
                        <button
                          key={option.id}
                          type="button"
                          onClick={() => onToggleTier(index, option.id)}
                          aria-pressed={selected}
                          className="rounded-full px-2 py-0.5 text-[11px]"
                          style={{
                            border: selected ? '1px solid var(--brand)' : '1px solid var(--line)',
                            background: selected ? 'var(--brand-weak, #EEF2FF)' : 'transparent',
                            color: selected ? 'var(--brand)' : 'var(--ink-500)',
                          }}
                        >
                          {selected ? `${position + 1}. ` : ''}{option.label}
                        </button>
                      );
                    })}
                  </div>
                  {group.tiers.length > 1 && (
                    <p className="text-[11px] mt-1" style={{ color: 'var(--ink-400)' }}>
                      Numbered in the order free places are given out: {group.tiers.map(labelFor).join(' → ')}.
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <label className="flex flex-col gap-1">
                    <span className="text-[11px]" style={{ color: 'var(--ink-500)' }}>
                      Max total (combined)
                    </span>
                    <input
                      type="number"
                      min={1}
                      value={group.max_total}
                      onChange={(e) => onChange(index, 'max_total', e.target.value)}
                      placeholder="8"
                      className="rounded px-2 py-1 text-xs"
                      style={{ border: '1px solid var(--line)' }}
                    />
                  </label>
                  <label className="flex flex-col gap-1">
                    <span className="text-[11px]" style={{ color: 'var(--ink-500)' }}>
                      Included free (optional)
                    </span>
                    <input
                      type="number"
                      min={0}
                      value={group.included}
                      onChange={(e) => onChange(index, 'included', e.target.value)}
                      placeholder="0"
                      className="rounded px-2 py-1 text-xs"
                      style={{ border: '1px solid var(--line)' }}
                    />
                  </label>
                </div>

                {problems.length > 0 && (
                  <ul className="text-[11px] mt-1 list-disc pl-4" style={{ color: '#DC2626' }}>
                    {problems.map((problem) => <li key={problem}>{problem}</li>)}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      )}

      <button
        type="button"
        onClick={onAdd}
        disabled={options.length === 0}
        className="btn"
        style={{ width: '100%', justifyContent: 'center', opacity: options.length === 0 ? 0.5 : 1 }}
      >
        <Plus size={13} />
        Add group
      </button>
    </div>
  );
};
